import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, Download, Info } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { ZcaDesgloseModal } from "@/components/ZcaDesgloseModal";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  downloadZcaPreciosCsv,
  getZcaCatalogo,
  getZcaPrecios,
  type ZcaPrecioRow,
  type ZcaPreciosFilters,
} from "@/lib/api";
import { formatDate } from "@/lib/utils";

type Granularidad = "auto" | "horaria" | "diaria";
type Mercado = "MDA" | "MTR";

const STORAGE_KEY = "portal-edocta:zca-selected";

// Paleta para distinguir ZCAs.
const COLORS = [
  "#2563eb", "#dc2626", "#16a34a", "#ca8a04", "#9333ea",
  "#0891b2", "#ea580c", "#db2777", "#65a30d", "#475569",
];

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function isoLocal(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function currentMonthRange(): { desde: string; hasta: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { desde: isoLocal(first), hasta: isoLocal(last) };
}

function loadStored(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
}

export function ZcaPage() {
  const defaultRange = useMemo(currentMonthRange, []);
  const [desde, setDesde] = useState<string>(defaultRange.desde);
  const [hasta, setHasta] = useState<string>(defaultRange.hasta);
  const [selected, setSelected] = useState<string[]>(loadStored);
  const [granularidad, setGranularidad] = useState<Granularidad>("auto");
  const [mercados, setMercados] = useState<Mercado[]>(["MDA", "MTR"]);
  const [downloading, setDownloading] = useState(false);
  const [sortKey, setSortKey] = useState<keyof ZcaPrecioRow>("fecha");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);
  const [desglose, setDesglose] = useState<{
    zca: string;
    mercado: "MDA" | "MTR";
    fecha: string;
    hora: number | null;
  } | null>(null);

  const PAGE_SIZE = 100;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selected));
    } catch {
      /* ignorar storage lleno/deshabilitado */
    }
  }, [selected]);

  const catalogo = useQuery({
    queryKey: ["zca-catalogo"],
    queryFn: getZcaCatalogo,
  });

  const filters: ZcaPreciosFilters = useMemo(
    () => ({
      zcas: selected,
      desde: desde || undefined,
      hasta: hasta || undefined,
      mercados,
      granularidad,
    }),
    [selected, desde, hasta, mercados, granularidad],
  );

  const precios = useQuery({
    queryKey: ["zca-precios", filters],
    queryFn: () => getZcaPrecios(filters),
    enabled: selected.length > 0 && mercados.length > 0,
  });

  const chartData = useMemo(() => buildSeries(precios.data?.items ?? []), [precios.data]);

  const sortedRows = useMemo(() => {
    const rows = precios.data?.items ?? [];
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      // Comparar como número si las cadenas representan números (precio, ene, ...)
      const an = Number(av);
      const bn = Number(bv);
      if (!Number.isNaN(an) && !Number.isNaN(bn) && String(av) !== "" && String(bv) !== "") {
        return (an - bn) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [precios.data, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => sortedRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [sortedRows, page],
  );

  const handleSort = (key: keyof ZcaPrecioRow) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  };

  // Reset page cuando cambia el dataset
  useEffect(() => {
    setPage(0);
  }, [precios.data]);

  const toggleZca = (z: string) => {
    setSelected((s) => (s.includes(z) ? s.filter((x) => x !== z) : [...s, z]));
  };

  const toggleMercado = (m: Mercado) => {
    setMercados((arr) => (arr.includes(m) ? arr.filter((x) => x !== m) : [...arr, m]));
  };

  const colorFor = (zca: string): string => {
    const idx = (catalogo.data?.items ?? []).findIndex((c) => c.zona_carga_agrupada === zca);
    return COLORS[(idx >= 0 ? idx : 0) % COLORS.length];
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Field label="Desde">
            <input
              type="date"
              className="border rounded px-2 py-1.5 w-full"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </Field>
          <Field label="Hasta">
            <input
              type="date"
              className="border rounded px-2 py-1.5 w-full"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </Field>
          <Field label="Granularidad">
            <select
              className="border rounded px-2 py-1.5 w-full"
              value={granularidad}
              onChange={(e) => setGranularidad(e.target.value as Granularidad)}
            >
              <option value="auto">Auto (diaria si &gt; 7 días)</option>
              <option value="horaria">Horaria</option>
              <option value="diaria">Diaria (promedio)</option>
            </select>
          </Field>
          <Field label="Mercado">
            <div className="inline-flex border rounded overflow-hidden w-full">
              {(["MDA", "MTR"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleMercado(m)}
                  className={`flex-1 px-3 py-1.5 text-sm ${
                    mercados.includes(m)
                      ? "bg-blue-600 text-white"
                      : "bg-white hover:bg-slate-50"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </Field>
          <div className="flex items-end gap-2">
            <button
              type="button"
              disabled={selected.length === 0 || mercados.length === 0 || downloading}
              onClick={async () => {
                setDownloading(true);
                try {
                  await downloadZcaPreciosCsv(filters);
                } catch (err) {
                  alert(`Error al descargar: ${(err as Error).message}`);
                } finally {
                  setDownloading(false);
                }
              }}
              className="inline-flex items-center gap-1 border rounded px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex-1"
              title="Descargar resultado completo en CSV"
            >
              <Download className="w-4 h-4" />
              {downloading ? "…" : "CSV"}
            </button>
            <button
              type="button"
              onClick={() => {
                const r = currentMonthRange();
                setDesde(r.desde);
                setHasta(r.hasta);
                setSelected([]);
              }}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Limpiar
            </button>
          </div>
        </div>

        <div>
          <span className="text-xs font-medium text-slate-600">
            Zonas de carga agrupadas ({selected.length} seleccionadas)
          </span>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {catalogo.isLoading && (
              <span className="text-sm text-slate-500">Cargando…</span>
            )}
            {catalogo.data?.items.map((c) => {
              const isSel = selected.includes(c.zona_carga_agrupada);
              return (
                <button
                  key={c.zona_carga_agrupada}
                  type="button"
                  onClick={() => toggleZca(c.zona_carga_agrupada)}
                  className={`text-xs rounded px-2 py-1 border ${
                    isSel
                      ? "border-transparent text-white"
                      : "border-slate-300 hover:bg-slate-50"
                  }`}
                  style={
                    isSel
                      ? { backgroundColor: colorFor(c.zona_carga_agrupada) }
                      : undefined
                  }
                  title={`${c.zonas_pnd} zonas PND · Σ ${Number(c.suma_porcentaje).toFixed(2)}%`}
                >
                  {c.zona_carga_agrupada}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-4">
        {selected.length === 0 && (
          <div className="py-16 text-center text-slate-500">
            Selecciona al menos una zona de carga agrupada arriba.
          </div>
        )}
        {selected.length > 0 && precios.isLoading && (
          <div className="py-16 text-center text-slate-500">Cargando precios…</div>
        )}
        {precios.isError && (
          <div className="py-16 text-center text-red-600">
            Error: {(precios.error as Error).message}
          </div>
        )}
        {precios.data && chartData.points.length === 0 && (
          <div className="py-16 text-center text-slate-500">
            Sin datos para los filtros aplicados.
          </div>
        )}
        {precios.data && chartData.points.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-2 text-sm">
              <span className="text-slate-600">
                Granularidad: <strong>{precios.data.granularidad}</strong> · {chartData.points.length} puntos
              </span>
              <span className="text-xs text-slate-500">
                MDA línea sólida · MTR línea punteada
              </span>
            </div>
            <div style={{ width: "100%", height: 480 }}>
              <ResponsiveContainer>
                <LineChart data={chartData.points} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="x"
                    tick={{ fontSize: 11 }}
                    minTickGap={20}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    width={70}
                    tickFormatter={(v) => `$${Number(v).toFixed(0)}`}
                  />
                  <Tooltip
                    formatter={(v: number | string) =>
                      typeof v === "number" ? `$${v.toFixed(2)}/MWh` : v
                    }
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {chartData.series.map((s) => (
                    <Line
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      name={s.label}
                      stroke={colorFor(s.zca)}
                      strokeDasharray={s.mercado === "MTR" ? "5 3" : undefined}
                      dot={false}
                      strokeWidth={1.5}
                      connectNulls
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>

      {precios.data && precios.data.items.length > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <ThSort col="zca" label="ZCA" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <ThSort col="mercado" label="Mercado" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <ThSort col="fecha" label="Fecha" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <ThSort col="hora" label="Hora" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                <ThSort col="precio" label="Precio" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                <ThSort col="ene" label="Energía" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                <ThSort col="per" label="Pérdidas" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                <ThSort col="cng" label="Congestión" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                <th className="px-3 py-2 font-medium text-right w-12"></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r, i) => (
                <tr key={i} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-1.5">
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
                      style={{ backgroundColor: colorFor(r.zca) }}
                    />
                    {r.zca}
                  </td>
                  <td className="px-3 py-1.5">
                    <span
                      className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                        r.mercado === "MDA"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-purple-100 text-purple-700"
                      }`}
                    >
                      {r.mercado}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">{formatDate(r.fecha)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {r.hora != null ? r.hora : "—"}
                  </td>
                  <td
                    className="px-3 py-1.5 text-right tabular-nums font-medium cursor-pointer hover:underline hover:text-blue-700"
                    onClick={() =>
                      setDesglose({
                        zca: r.zca,
                        mercado: r.mercado,
                        fecha: r.fecha,
                        hora: r.hora,
                      })
                    }
                    title="Ver desglose del cálculo"
                  >
                    {fmtPrecio(r.precio)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtPrecio(r.ene)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtPrecio(r.per)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtPrecio(r.cng)}</td>
                  <td className="px-3 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() =>
                        setDesglose({
                          zca: r.zca,
                          mercado: r.mercado,
                          fecha: r.fecha,
                          hora: r.hora,
                        })
                      }
                      className="text-slate-400 hover:text-blue-700"
                      title="Ver desglose del cálculo"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm border-t px-3 py-2">
              <div className="text-slate-500">
                {sortedRows.length.toLocaleString("es-MX")} filas · página {page + 1} de {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="border rounded px-3 py-1 disabled:opacity-50"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  ← Anterior
                </button>
                <button
                  type="button"
                  className="border rounded px-3 py-1 disabled:opacity-50"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page + 1 >= totalPages}
                >
                  Siguiente →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {desglose && (
        <ZcaDesgloseModal
          open
          zca={desglose.zca}
          mercado={desglose.mercado}
          fecha={desglose.fecha}
          hora={desglose.hora}
          onClose={() => setDesglose(null)}
        />
      )}
    </div>
  );
}

const PRECIO_FMT = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function fmtPrecio(v: string | null): string {
  if (v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return PRECIO_FMT.format(n);
}

function ThSort({
  col,
  label,
  sortKey,
  sortDir,
  onSort,
  align,
}: {
  col: keyof ZcaPrecioRow;
  label: string;
  sortKey: keyof ZcaPrecioRow;
  sortDir: "asc" | "desc";
  onSort: (k: keyof ZcaPrecioRow) => void;
  align?: "right";
}) {
  const active = sortKey === col;
  return (
    <th
      className={`px-3 py-2 font-medium select-none cursor-pointer hover:bg-slate-200 ${
        align === "right" ? "text-right" : ""
      }`}
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {!active && <ArrowUpDown className="w-3 h-3 opacity-40" />}
        {active && sortDir === "asc" && <ArrowUp className="w-3 h-3" />}
        {active && sortDir === "desc" && <ArrowDown className="w-3 h-3" />}
      </span>
    </th>
  );
}

type Series = { key: string; label: string; zca: string; mercado: "MDA" | "MTR" };

/**
 * Pivot de filas (zca, mercado, fecha, hora, precio) a estructura plana
 * que Recharts puede graficar — un objeto por punto X con una key por línea.
 */
function buildSeries(
  rows: { zca: string; mercado: "MDA" | "MTR"; fecha: string; hora: number | null; precio: string | null }[],
): { points: Record<string, string | number>[]; series: Series[] } {
  if (rows.length === 0) return { points: [], series: [] };

  const seriesMap = new Map<string, Series>();
  const pointMap = new Map<string, Record<string, string | number>>();

  for (const r of rows) {
    const key = `${r.zca}__${r.mercado}`;
    if (!seriesMap.has(key)) {
      seriesMap.set(key, {
        key,
        label: `${r.zca} (${r.mercado})`,
        zca: r.zca,
        mercado: r.mercado,
      });
    }

    const x = r.hora != null ? `${r.fecha} ${pad(r.hora)}h` : r.fecha;
    if (!pointMap.has(x)) pointMap.set(x, { x });
    const point = pointMap.get(x)!;
    if (r.precio != null) {
      point[key] = Number(r.precio);
    }
  }

  // Ordenar puntos por la X original (ya viene ordenada del backend, pero
  // por seguridad la ordeno aquí).
  const points = Array.from(pointMap.values()).sort((a, b) =>
    String(a.x).localeCompare(String(b.x)),
  );

  return { points, series: Array.from(seriesMap.values()) };
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

